import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Save,
  Edit,
  Notifications,
  Security,
  ArrowForward,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Add,
  Delete,
  VpnKey,
} from "@mui/icons-material";

// Mock user data
const MOCK_USER = {
  id: 1,
  name: "John Doe",
  email: "john.doe@example.com",
  phone: "+1 (555) 123-4567",
  avatar: null,
  settings: {
    emailNotifications: true,
    smsNotifications: false,
    marketAlerts: true,
    twoFactorAuth: false,
    darkMode: true,
    timezone: "America/New_York",
  },
  apiKeys: [
    {
      id: "api_key_1",
      name: "Trading Bot",
      key: "sk_live_fj84jf948fj39fj39fd9",
      created: "2025-04-01",
      lastUsed: "2025-05-15",
      status: "active",
    },
    {
      id: "api_key_2",
      name: "Portfolio Tracker",
      key: "sk_live_39fj39fjd93j3f843j3f",
      created: "2025-03-15",
      lastUsed: "2025-04-20",
      status: "active",
    },
  ],
};

function UserSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(MOCK_USER);
  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState({
    name: user.name,
    email: user.email,
    phone: user.phone,
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  const [newKeyName, setNewKeyName] = useState("");
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState("");

  // Function to generate consistent colors from user name
  function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  }

  // Function to get initials from a name
  function getInitials(name) {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .substring(0, 2);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleToggleChange = (setting) => {
    setUser({
      ...user,
      settings: {
        ...user.settings,
        [setting]: !user.settings[setting],
      },
    });
  };

  const handleSaveProfile = () => {
    setUser({
      ...user,
      name: formValues.name,
      email: formValues.email,
      phone: formValues.phone,
    });
    setEditMode(false);
    setSuccessMessage("Profile updated successfully");

    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        mb: 3, p: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center',
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
          Account Settings
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Profile Information</Typography>
              <IconButton
                color="primary"
                onClick={() => setEditMode(!editMode)}
              >
                <Edit />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", mb: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mr: 2,
                  bgcolor: stringToColor(user.name),
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              <Box>
                <Typography variant="h5">{user.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  User ID: {user.id}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={!editMode}
                >
                  Change Photo
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "filled"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email Address"
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "filled"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone Number"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                  variant={editMode ? "outlined" : "filled"}
                />
              </Grid>
            </Grid>

            {editMode && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveProfile}
                >
                  Save Changes
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Notifications sx={{ mr: 1 }} />
              <Typography variant="h6">Notification Settings</Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <List>
              <ListItem>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive important updates via email"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={user.settings.emailNotifications}
                    onChange={() => handleToggleChange("emailNotifications")}
                    edge="end"
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="SMS Notifications"
                  secondary="Receive urgent alerts via text message"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={user.settings.smsNotifications}
                    onChange={() => handleToggleChange("smsNotifications")}
                    edge="end"
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Market Alerts"
                  secondary="Get notified about significant market movements"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={user.settings.marketAlerts}
                    onChange={() => handleToggleChange("marketAlerts")}
                    edge="end"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>

          {/* Security Settings */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Security sx={{ mr: 1 }} />
              <Typography variant="h6">Security Settings</Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <List>
              <ListItem>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary="Add an extra layer of security to your account"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={user.settings.twoFactorAuth}
                    onChange={() => handleToggleChange("twoFactorAuth")}
                    edge="end"
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem sx={{ cursor: "pointer" }}>
                <ListItemText
                  primary="Change Password"
                  secondary="Update your account password"
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end">
                    <ArrowForward />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem
                sx={{ cursor: "pointer" }}
                onClick={() => setApiKeyDialogOpen(true)}
              >
                <ListItemText
                  primary="API Keys"
                  secondary="Manage your API keys for external connections"
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => setApiKeyDialogOpen(true)}
                  >
                    <ArrowForward />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem
                sx={{ cursor: "pointer" }}
                onClick={() => navigate("/user/api-connections")}
              >
                <ListItemText
                  primary="API Connections"
                  secondary="Configure broker, market data, and AI service APIs"
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => navigate("/user/api-connections")}
                  >
                    <ArrowForward />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Platform Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Platform Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={user.settings.darkMode}
                      onChange={() => handleToggleChange("darkMode")}
                    />
                  }
                  label="Dark Mode"
                />
                <Typography variant="body2" color="text.secondary">
                  Switch between light and dark theme
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Timezone"
                  value={user.settings.timezone}
                  onChange={(e) => {
                    setUser({
                      ...user,
                      settings: {
                        ...user.settings,
                        timezone: e.target.value,
                      },
                    });
                  }}
                  fullWidth
                  variant="outlined"
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">
                    Central European Time (CET)
                  </option>
                  <option value="Asia/Tokyo">Japan (JST)</option>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* API Keys Dialog */}
      <Dialog
        open={apiKeyDialogOpen}
        onClose={() => setApiKeyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <VpnKey sx={{ mr: 1 }} />
            <Typography variant="h6">API Keys</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            API keys allow external applications to access your account
            securely. Keep your keys secret and never share them publicly.
          </Typography>

          {newlyGeneratedKey && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Box>
                <Typography variant="subtitle2">
                  New API key generated!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: "break-all",
                    fontFamily: "monospace",
                    my: 1,
                  }}
                >
                  {newlyGeneratedKey}
                </Typography>
                <Typography variant="caption" color="error">
                  This key will only be shown once. Copy it now and store it
                  securely.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button
                    startIcon={<ContentCopy />}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      navigator.clipboard.writeText(newlyGeneratedKey);
                      setSuccessMessage("API key copied to clipboard");
                      setTimeout(() => setSuccessMessage(""), 3000);
                    }}
                  >
                    Copy to clipboard
                  </Button>
                </Box>
              </Box>
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.apiKeys.map((apiKey) => (
                  <TableRow key={apiKey.id}>
                    <TableCell>{apiKey.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body2" fontFamily="monospace">
                          {showApiKey[apiKey.id]
                            ? apiKey.key
                            : `${apiKey.key.substring(0, 8)}...${apiKey.key.substring(apiKey.key.length - 4)}`}
                        </Typography>
                        <Tooltip
                          title={
                            showApiKey[apiKey.id] ? "Hide key" : "Show key"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              setShowApiKey((prev) => ({
                                ...prev,
                                [apiKey.id]: !prev[apiKey.id],
                              }))
                            }
                          >
                            {showApiKey[apiKey.id] ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy to clipboard">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(apiKey.key);
                              setSuccessMessage("API key copied to clipboard");
                              setTimeout(() => setSuccessMessage(""), 3000);
                            }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{apiKey.created}</TableCell>
                    <TableCell>{apiKey.lastUsed}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          apiKey.status === "active" ? "Active" : "Inactive"
                        }
                        color={
                          apiKey.status === "active" ? "success" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          const updatedApiKeys = user.apiKeys.filter(
                            (key) => key.id !== apiKey.id,
                          );
                          setUser({ ...user, apiKeys: updatedApiKeys });
                          setSuccessMessage("API key revoked successfully");
                          setTimeout(() => setSuccessMessage(""), 3000);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {user.apiKeys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        You don't have any API keys yet. Generate one to get
                        started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Generate New API Key
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <TextField
                  label="Key Name"
                  placeholder="e.g., Trading Bot, Portfolio Tracker"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={
                    generatingKey ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Add />
                    )
                  }
                  disabled={!newKeyName.trim() || generatingKey}
                  onClick={() => {
                    setGeneratingKey(true);
                    // Simulate API key generation
                    setTimeout(() => {
                      const newKey = `sk_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
                      const newApiKey = {
                        id: `api_key_${Date.now()}`,
                        name: newKeyName,
                        key: newKey,
                        created: new Date().toISOString().split("T")[0],
                        lastUsed: "Never",
                        status: "active",
                      };

                      setUser({
                        ...user,
                        apiKeys: [...user.apiKeys, newApiKey],
                      });

                      setNewlyGeneratedKey(newKey);
                      setNewKeyName("");
                      setGeneratingKey(false);
                    }, 1500);
                  }}
                >
                  Generate New Key
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Security Notice
          </Typography>
          <Typography variant="body2" color="text.secondary">
            API keys provide full access to your account. Always store them
            securely and never expose them in client-side code or public
            repositories. If you suspect a key has been compromised, revoke it
            immediately and generate a new one.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApiKeyDialogOpen(false);
              setNewlyGeneratedKey("");
              setShowApiKey({});
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserSettings;
